import {
  createStyles,
  InputAdornment,
  TextField,
  Typography,
  WithStyles,
  withStyles,
} from "@material-ui/core";
import { useTranslations } from "adapters/i18n";
import React, { type ChangeEvent } from "react";
import type { LivestockInfoFromFarm } from "shared/types";
import { parseWeightInput } from "../../../utils/lots.utils";

type MeasuredWeightColumnCellProps = {
  error: string | null | undefined;
  onBlur: (row: LivestockInfoFromFarm) => void;
  onChange: (weight: number | null, row: LivestockInfoFromFarm) => void;
  row: LivestockInfoFromFarm;
  value: number | null;
} & WithStyles<typeof styles>;

function MeasuredWeightColumnCell({
  classes,
  error,
  onBlur,
  onChange,
  row,
  value,
}: MeasuredWeightColumnCellProps) {
  const { t } = useTranslations();

  const parsedValue = value ? value.toString() : "";

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const weight = parseWeightInput(event.target.value);
    onChange(weight, row);
  };

  const handleBlur = () => {
    onBlur(row);
  };

  return (
    <div className={classes.root}>
      <TextField
        aria-invalid={!!error}
        className={classes.input}
        data-testid="lot-measured-weight-input"
        error={!!error}
        helperText={error}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {t("common:measure.KG")}
            </InputAdornment>
          ),
        }}
        inputProps={{ min: 0 }}
        onBlur={handleBlur}
        onChange={handleChange}
        placeholder={t("livestock:weight")}
        type="number"
        value={parsedValue}
      />

      <Typography
        className={classes.text}
        component="span"
        data-testid="lot-measured-weight-text"
      >
        {value ?? "-"}
      </Typography>
    </div>
  );
}

const styles = () =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      height: 84,
      justifyContent: "center",
      position: "relative",
      width: "100px",
      "@media print": {
        width: "min-content",
      },
    },
    input: {
      marginTop: "14px",
      "@media print": {
        display: "none",
      },
    },
    text: {
      display: "none",
      "@media print": {
        display: "block",
        fontSize: "inherit",
      },
    },
  });

export default withStyles(styles)(MeasuredWeightColumnCell);
